import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";

// ============================================
// WEB3 / $MANGO HOOKS
// ============================================
// Client-side hooks for wallet connection, staking, and transactions
// Part of the Mango Group ecosystem

// Chain configuration
export const SUPPORTED_CHAINS = {
  // Mainnet
  1: { name: "Ethereum", symbol: "ETH", explorer: "https://etherscan.io" },
  8453: { name: "Base", symbol: "ETH", explorer: "https://basescan.org" },
  42161: { name: "Arbitrum", symbol: "ETH", explorer: "https://arbiscan.io" },
  137: { name: "Polygon", symbol: "MATIC", explorer: "https://polygonscan.com" },
  // Testnets
  11155111: { name: "Sepolia", symbol: "ETH", explorer: "https://sepolia.etherscan.io" },
  84532: { name: "Base Sepolia", symbol: "ETH", explorer: "https://sepolia.basescan.org" },
} as const;

export type SupportedChainId = keyof typeof SUPPORTED_CHAINS;

// $MANGO token addresses per chain
export const MANGO_TOKEN_ADDRESS: Partial<Record<SupportedChainId, string>> = {
  1: "0x...", // TODO: Add mainnet address
  8453: "0x...", // TODO: Add Base address
  84532: "0x...", // TODO: Add testnet address
};

// Staking contract addresses per chain
export const STAKING_CONTRACT_ADDRESS: Partial<Record<SupportedChainId, string>> = {
  1: "0x...", // TODO: Add mainnet address
  8453: "0x...", // TODO: Add Base address
  84532: "0x...", // TODO: Add testnet address
};

// Escrow contract addresses per chain
export const ESCROW_CONTRACT_ADDRESS: Partial<Record<SupportedChainId, string>> = {
  1: "0x...", // TODO: Add mainnet address
  8453: "0x...", // TODO: Add Base address
  84532: "0x...", // TODO: Add testnet address
};

// ============================================
// WALLET CONNECTION HOOK
// ============================================

export function useWalletConnection() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convex mutations
  const generateNonce = useMutation(api.web3.generateWalletNonce);
  const connectWalletMutation = useMutation(api.web3.connectWallet);
  const disconnectWalletMutation = useMutation(api.web3.disconnectWallet);

  // Convex queries
  const wallets = useQuery(api.web3.getMyWallets);

  /**
   * Request wallet connection and signature
   * This should be called after the user connects via their wallet provider
   */
  const connectWallet = useCallback(
    async (params: {
      walletAddress: string;
      chainId: number;
      walletType: "metamask" | "walletconnect" | "coinbase" | "other";
      signMessage: (message: string) => Promise<string>;
    }) => {
      setIsConnecting(true);
      setError(null);

      try {
        // 1. Generate nonce
        const { message, nonce } = await generateNonce({
          walletAddress: params.walletAddress,
        });

        // 2. Request signature from wallet
        const signature = await params.signMessage(message);

        // 3. Submit to backend
        const result = await connectWalletMutation({
          walletAddress: params.walletAddress,
          chainId: params.chainId,
          walletType: params.walletType,
          signature,
          nonce,
        });

        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Connection failed";
        setError(message);
        throw err;
      } finally {
        setIsConnecting(false);
      }
    },
    [generateNonce, connectWalletMutation]
  );

  /**
   * Disconnect a wallet
   */
  const disconnectWallet = useCallback(
    async (walletAddress: string) => {
      setError(null);
      try {
        await disconnectWalletMutation({ walletAddress });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Disconnect failed";
        setError(message);
        throw err;
      }
    },
    [disconnectWalletMutation]
  );

  const primaryWallet = useMemo(
    () => wallets?.find((w) => w.isPrimary) ?? null,
    [wallets]
  );

  const isConnected = useMemo(
    () => (wallets?.length ?? 0) > 0,
    [wallets]
  );

  return {
    wallets: wallets ?? [],
    primaryWallet,
    isConnected,
    isConnecting,
    error,
    connectWallet,
    disconnectWallet,
  };
}

// ============================================
// STAKING HOOK
// ============================================

export function useStaking() {
  const stakingInfo = useQuery(api.web3.getMyStakingInfo);
  const feeDiscount = useQuery(api.web3.getMyFeeDiscount);

  return useMemo(
    () => ({
      stakingInfo,
      feeDiscount: feeDiscount ?? { discount: 0, tier: "none" as const },
      isLoading: stakingInfo === undefined,
      tier: (stakingInfo?.tier ?? "none") as "none" | "bronze" | "silver" | "gold" | "platinum" | "diamond",
      totalStaked: stakingInfo?.totalStaked ?? "0",
      benefits: (stakingInfo?.benefits ?? []) as string[],
      nextTier: stakingInfo?.nextTier ?? null,
    }),
    [stakingInfo, feeDiscount]
  );
}

// ============================================
// TRANSACTIONS HOOK
// ============================================

export function useWeb3Transactions(options?: {
  limit?: number;
  type?: string;
}) {
  const queryArgs = useMemo(
    () => ({
      limit: options?.limit,
      type: options?.type,
    }),
    [options?.limit, options?.type]
  );

  const transactions = useQuery(api.web3.getMyTransactions, queryArgs);
  const recordTransaction = useMutation(api.web3.recordTransaction);

  return useMemo(
    () => ({
      transactions: transactions ?? [],
      isLoading: transactions === undefined,
      recordTransaction,
    }),
    [transactions, recordTransaction]
  );
}

// ============================================
// FEE CALCULATOR HOOK
// ============================================

export function useFeeCalculator() {
  const { feeDiscount, tier } = useStaking();

  const calculateFee = useCallback(
    (baseFeeUsd: number, payingInMango: boolean) => {
      // Base discount for paying in $MANGO
      let discount = payingInMango ? 50 : 0;

      // Add staking discount (replaces base discount if higher)
      if (feeDiscount.discount > discount) {
        discount = feeDiscount.discount;
      }

      const discountedFee = baseFeeUsd * (1 - discount / 100);

      return {
        baseFee: baseFeeUsd,
        discount,
        discountedFee,
        savings: baseFeeUsd - discountedFee,
        tier,
        payingInMango,
      };
    },
    [feeDiscount, tier]
  );

  return { calculateFee, tier, discount: feeDiscount.discount };
}

// ============================================
// ESCROW HOOK
// ============================================

export function useEscrow(adId?: string) {
  const recordTransaction = useMutation(api.web3.recordTransaction);

  /**
   * Create and fund an escrow for a transaction
   * This should be called after the on-chain transaction is submitted
   */
  const recordEscrowCreation = useCallback(
    async (params: {
      txHash: string;
      chainId: number;
      amount: string;
      escrowId: string;
    }) => {
      if (!adId) throw new Error("Ad ID required");

      return recordTransaction({
        txHash: params.txHash,
        chainId: params.chainId,
        type: "escrow_create",
        amount: params.amount,
        escrowId: params.escrowId,
        adId: adId as any, // TODO: Fix type
      });
    },
    [adId, recordTransaction]
  );

  return {
    recordEscrowCreation,
    recordTransaction,
  };
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format token amount from wei to human readable
 */
export function formatTokenAmount(
  amount: string | bigint,
  decimals = 18,
  displayDecimals = 4
): string {
  const value = typeof amount === "string" ? BigInt(amount) : amount;
  const divisor = BigInt(10 ** decimals);
  const whole = value / divisor;
  const fractional = value % divisor;

  if (fractional === BigInt(0)) {
    return whole.toString();
  }

  const fractionalStr = fractional.toString().padStart(decimals, "0");
  const trimmed = fractionalStr.slice(0, displayDecimals).replace(/0+$/, "");

  if (trimmed === "") {
    return whole.toString();
  }

  return `${whole}.${trimmed}`;
}

/**
 * Parse human readable amount to wei
 */
export function parseTokenAmount(amount: string, decimals = 18): bigint {
  const [whole, fractional = ""] = amount.split(".");
  const paddedFractional = fractional.padEnd(decimals, "0").slice(0, decimals);
  return BigInt(whole + paddedFractional);
}

/**
 * Shorten wallet address for display
 */
export function shortenAddress(address: string, chars = 4): string {
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
}

/**
 * Get explorer URL for a transaction or address
 */
export function getExplorerUrl(
  chainId: number,
  hashOrAddress: string,
  type: "tx" | "address" = "tx"
): string {
  const chain = SUPPORTED_CHAINS[chainId as SupportedChainId];
  if (!chain) return "";

  return `${chain.explorer}/${type}/${hashOrAddress}`;
}

/**
 * Check if a chain is supported
 */
export function isSupportedChain(chainId: number): chainId is SupportedChainId {
  return chainId in SUPPORTED_CHAINS;
}

/**
 * Get chain name
 */
export function getChainName(chainId: number): string {
  const chain = SUPPORTED_CHAINS[chainId as SupportedChainId];
  return chain?.name ?? `Chain ${chainId}`;
}

// ============================================
// TYPES
// ============================================

export interface WalletConnection {
  _id: string;
  walletAddress: string;
  chainId: number;
  walletType: "metamask" | "walletconnect" | "coinbase" | "other";
  isVerified: boolean;
  isPrimary: boolean;
  connectedAt: number;
  lastUsedAt: number;
  stake: {
    amount: string;
    tier: string;
    feeDiscount: number;
    pendingRewards: string;
  } | null;
}

export interface StakingTier {
  name: "none" | "bronze" | "silver" | "gold" | "platinum" | "diamond";
  discount: number;
  benefits: string[];
}

export interface FeeCalculation {
  baseFee: number;
  discount: number;
  discountedFee: number;
  savings: number;
  tier: string;
  payingInMango: boolean;
}
